import React, { useRef, useState, useEffect, useCallback } from 'react';
import { Box, Typography, Button } from '@mui/material';
import { Delete, CheckCircleOutline } from '@mui/icons-material';

const SignaturePad = ({ onSave, disabled = false }) => {
  const canvasRef = useRef(null);
  const [isDrawing, setIsDrawing] = useState(false);
  const [hasSignature, setHasSignature] = useState(false);
  const lastPoint = useRef(null);

  // Set up canvas dimensions
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const resizeCanvas = () => {
      const rect = canvas.parentElement.getBoundingClientRect();
      const width = Math.min(rect.width, 500);
      const height = 200;

      // Store current drawing data before resize
      const tempCanvas = document.createElement('canvas');
      tempCanvas.width = canvas.width;
      tempCanvas.height = canvas.height;
      const tempCtx = tempCanvas.getContext('2d');
      if (canvas.width > 0 && canvas.height > 0) {
        tempCtx.drawImage(canvas, 0, 0);
      }

      canvas.width = width;
      canvas.height = height;

      const ctx = canvas.getContext('2d');
      ctx.lineCap = 'round';
      ctx.lineJoin = 'round';
      ctx.strokeStyle = '#000';
      ctx.lineWidth = 2;

      // Restore previous drawing if any
      if (canvas.width > 0 && canvas.height > 0 && hasSignature) {
        ctx.drawImage(tempCanvas, 0, 0, canvas.width, canvas.height);
      }
    };

    resizeCanvas();
    window.addEventListener('resize', resizeCanvas);
    return () => window.removeEventListener('resize', resizeCanvas);
  }, [hasSignature]);

  const getCoordinates = useCallback((e) => {
    const canvas = canvasRef.current;
    if (!canvas) return null;

    const rect = canvas.getBoundingClientRect();
    let clientX, clientY;

    if (e.touches) {
      // Touch event
      clientX = e.touches[0].clientX;
      clientY = e.touches[0].clientY;
      e.preventDefault();
    } else {
      // Mouse event
      clientX = e.clientX;
      clientY = e.clientY;
    }

    return {
      x: clientX - rect.left,
      y: clientY - rect.top,
    };
  }, []);

  const startDrawing = useCallback((e) => {
    if (disabled) return;
    e.preventDefault();
    const coords = getCoordinates(e);
    if (!coords) return;

    setIsDrawing(true);
    lastPoint.current = coords;

    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    ctx.beginPath();
    ctx.moveTo(coords.x, coords.y);
    ctx.lineTo(coords.x, coords.y);
    ctx.stroke();
    setHasSignature(true);
  }, [disabled, getCoordinates]);

  const draw = useCallback((e) => {
    if (!isDrawing || disabled) return;
    e.preventDefault();

    const coords = getCoordinates(e);
    if (!coords || !lastPoint.current) return;

    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');

    ctx.beginPath();
    ctx.moveTo(lastPoint.current.x, lastPoint.current.y);
    ctx.lineTo(coords.x, coords.y);
    ctx.stroke();

    lastPoint.current = coords;
  }, [isDrawing, disabled, getCoordinates]);

  const stopDrawing = useCallback((e) => {
    if (e) e.preventDefault();
    setIsDrawing(false);
    lastPoint.current = null;
  }, []);

  const clearSignature = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    setHasSignature(false);
    onSave(null);
  }, [onSave]);

  const saveSignature = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const dataUrl = canvas.toDataURL('image/png');
    onSave(dataUrl);
  }, [onSave]);

  return (
    <Box>
      <Box
        sx={{
          position: 'relative',
          border: '2px solid',
          borderColor: hasSignature ? 'primary.main' : 'grey.300',
          borderRadius: 2,
          overflow: 'hidden',
          bgcolor: '#fff',
          cursor: disabled ? 'not-allowed' : 'crosshair',
          touchAction: 'none',
        }}
      >
        <canvas
          ref={canvasRef}
          style={{
            display: 'block',
            width: '100%',
            height: '200px',
          }}
          onMouseDown={startDrawing}
          onMouseMove={draw}
          onMouseUp={stopDrawing}
          onMouseLeave={stopDrawing}
          onTouchStart={startDrawing}
          onTouchMove={draw}
          onTouchEnd={stopDrawing}
        />

        {!hasSignature && (
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{
              position: 'absolute',
              bottom: 8,
              left: '50%',
              transform: 'translateX(-50%)',
              pointerEvents: 'none',
              opacity: 0.6,
            }}
          >
            Sign here
          </Typography>
        )}
      </Box>

      <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 1 }}>
        <Box>
          {hasSignature && (
            <Button
              size="small"
              color="error"
              startIcon={<Delete />}
              onClick={clearSignature}
              disabled={disabled}
            >
              Clear
            </Button>
          )}
        </Box>
        {hasSignature && (
          <Button
            size="small"
            color="primary"
            variant="contained"
            startIcon={<CheckCircleOutline />}
            onClick={saveSignature}
            disabled={disabled}
          >
            Confirm Signature
          </Button>
        )}
      </Box>
    </Box>
  );
};

export default SignaturePad;